import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { defaultEducation, educationSchema } from "@reactive-resume/schema";
import {
  Button,
  DatePicker,
  DateRangePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { AiActions } from "@/client/components/ai-actions";
import { chinaDegrees, getDegreeLabel } from "@/client/data/china-degrees";

import { SectionDialog } from "../sections/shared/section-dialog";
import { URLInput } from "../sections/shared/url-input";

const formSchema = educationSchema;

type FormValues = z.infer<typeof formSchema>;

export const EducationDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: defaultEducation,
    resolver: zodResolver(formSchema),
  });

  return (
    <SectionDialog<FormValues> id="education" form={form} defaultValues={defaultEducation}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          name="institution"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Institution`}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="studyType"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t({
                  message: "Type of Study",
                  comment: "For example, Bachelor's Degree or Master's Degree",
                })}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="area"
          control={form.control}
          render={({ field }) => {
            const { i18n } = useLingui();
            const isChinese = i18n.locale.startsWith("zh");
            
            if (isChinese) {
              return (
                <FormItem>
                  <FormLabel>学历</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="请选择学历" />
                      </SelectTrigger>
                      <SelectContent>
                        {chinaDegrees.map((degree) => (
                          <SelectItem key={degree.value} value={degree.value}>
                            {degree.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }
            
            return (
              <FormItem>
                <FormLabel>
                  {t({
                    message: "Area of Study",
                    comment: "For example, Computer Science or Business Administration",
                  })}
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          name="score"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t({
                  message: "Score",
                  comment: "Score or honors for the degree, for example, CGPA or magna cum laude",
                })}
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="9.2 GPA" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="date"
          control={form.control}
          render={({ field }) => {
            const { i18n } = useLingui();
            const isChinese = i18n.locale.startsWith("zh");
            
            if (isChinese) {
              // 解析日期范围值
              const parseDateRange = (dateStr: string) => {
                console.log('Education parseDateRange - Parsing date string:', dateStr);
                
                if (!dateStr || dateStr.trim() === '') {
                  console.log('Education parseDateRange - Empty date string, returning empty start/end');
                  return { start: '', end: '' };
                }

                const trimmed = dateStr.trim();
                
                // Check if it's a date range (contains '至')
                if (trimmed.includes('至')) {
                  const [start, end] = trimmed.split('至');
                  console.log('Education parseDateRange - Parsed as date range:', { start: start?.trim() || '', end: end?.trim() || '' });
                  return { start: start?.trim() || '', end: end?.trim() || '' };
                } else {
                  // Single date - return the complete date as start date with empty end
                  console.log('Education parseDateRange - Parsed as single date:', { start: trimmed, end: '' });
                  return { start: trimmed, end: '' };
                }
              };
              
              const { start, end } = parseDateRange(field.value || "");
              console.log('Current field value:', field.value, 'Parsed start:', start, 'Parsed end:', end);
              
              return (
                <FormItem className="sm:col-span-2">
                  <FormLabel>日期或日期范围</FormLabel>
                  <FormControl>
                    <DateRangePicker
                      startValue={start}
                      endValue={end}
                      onChange={(startDate, endDate) => {
                        console.log('DateRangePicker onChange:', { startDate, endDate });
                        if (startDate && endDate) {
                          field.onChange(`${startDate}至${endDate}`);
                        } else if (startDate && !endDate) {
                          field.onChange(startDate);
                        } else if (!startDate && !endDate) {
                          field.onChange("");
                        }
                      }}
                      placeholder="选择日期范围"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }
            
            return (
              <FormItem className="sm:col-span-2">
                <FormLabel>{t`Date or Date Range`}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t`March 2023 - Present`} />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          name="url"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Website`}</FormLabel>
              <FormControl>
                <URLInput {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="summary"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Summary`}</FormLabel>
              <FormControl>
                <RichInput
                  {...field}
                  content={field.value}
                  footer={(editor) => (
                    <AiActions
                      value={editor.getText()}
                      onChange={(value) => {
                        editor.commands.setContent(value, true);
                        field.onChange(value);
                      }}
                    />
                  )}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </SectionDialog>
  );
};
